import { useContext, useState } from "react";
import { SocialCampaignContext } from "../Context";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Stack,
  SvgIcon,
  Typography,
} from "@mui/material";
import CustomAutoCompleteNew from "global/components/CustomAutoCompleteNew/CustomAutoCompleteNew";
import { isTruthy } from "helpers/methods";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { cloneDeep } from "lodash";
import strings from "global/constants/StringConstants";
import CustomDialog from "global/components/CustomDialog/CustomDialog";

interface CustomProps {
  formFields: any;
  setFormFields: Function;
}

const PostSection = (props: CustomProps) => {
  const {
    managingStep,
    setManagingStep,
    submitCampaign,
    setSubmitCampaign,
    pages,
    setPages,
    socialApps,
    pageDetails,
  } = useContext(SocialCampaignContext);

  const { formFields, setFormFields } = props;
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<{
    label: string;
    value: string;
  } | null>(null);

  const stepCompletion = () => {
    return (
      isTruthy(submitCampaign.socialAppName) &&
      isTruthy(submitCampaign.socialSource)
    );
  };

  const handlePlatFormChange = (
    event: any,
    newValue: { label: string; value: string }
  ) => {
    if (formFields.selectedPlatform.value !== newValue.value) {
      setSelectedPlatform(newValue);
      setOpenConfirmation(true);
    } else {
      applyPlatformChange(newValue);
    }
  };

  const applyPlatformChange = (newValue: { label: string; value: string }) => {
    setOpenConfirmation(false);
    const pageList = pageDetails?.metaData?.pageDetails || [];

    const filteredPages = pageList
      .filter((page: any) =>
        newValue.value === strings.INSTAGRAM
          ? page.instagram_business_account
          : true
      )
      .map((page: any) => {
        if (newValue.value === strings.INSTAGRAM) {
          const instagramAccount = page.instagram_business_account;
          return {
            label: instagramAccount.username,
            value: instagramAccount.id,
            profile_picture: instagramAccount.profile_picture_url,
            userName: instagramAccount.username,
          };
        } else {
          return {
            label: page.name,
            value: page.id,
            profile_picture: pageDetails?.metaData.profilePicture ?? "",
            userName: pageDetails?.metaData.userName ?? "",
          };
        }
      });

    setPages(filteredPages);
    setFormFields((prevFormFields: any) => {
      const isPlatformChanged =
        prevFormFields.selectedPlatform.value !== newValue.value;
      return {
        ...prevFormFields,
        selectedPlatform: {
          error: "",
          value: newValue?.value || "",
        },
        ...(isPlatformChanged && {
          selectedPage: {
            label: {
              error: "",
              value: "",
            },
            userName: {
              error: "",
              value: "",
            },
            pageId: {
              error: "",
              value: "",
            },
            profilePicture: {
              error: "",
              value: "",
            },
          },
          message: {
            value: "",
            error: "",
          },
          image: {
            value: [],
            error: "",
          },
        }),
      };
    });
    setSubmitCampaign((prev: any) => {
      const isPlatformChanged = prev.socialAppName !== newValue.value;
      return {
        ...prev,
        socialCampaign: {
          ...submitCampaign.socialCampaign,
          link: isPlatformChanged ? [] : submitCampaign.socialCampaign.link,
          message: isPlatformChanged
            ? ""
            : submitCampaign.socialCampaign.message,
        },
      };
    });
  };

  const handleSave = () => {
    let error = false;
    const clonedFormFields = cloneDeep(formFields);
    if (!isTruthy(clonedFormFields.name.value)) {
      error = true;
      clonedFormFields.name.error = "Please enter the campaign name";
    }
    if (!isTruthy(clonedFormFields.selectedPlatform.value)) {
      error = true;
      clonedFormFields.selectedPlatform.error = "Please select platform";
    }
    if (!isTruthy(clonedFormFields.selectedPage.label.value)) {
      error = true;
      clonedFormFields.selectedPage.label.error =
        "Please select page to post on";
    }
    if (error) {
      return setFormFields(clonedFormFields);
    }

    setSubmitCampaign({
      ...submitCampaign,
      name: formFields.name.value,
      socialAppName: formFields.selectedPlatform.value,
      socialSource: formFields.selectedPage.userName.value,
      profilePicture: formFields.selectedPage.profilePicture.value,
      userName: formFields.selectedPage.userName.value,
      pageName: formFields.selectedPage.label.value,
      socialCampaign: {
        ...submitCampaign.socialCampaign,
        pageId: formFields.selectedPage.pageId.value,
      },
    });
    setManagingStep("");
  };

  const getExpandedView = () => {
    return (
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" spacing={4} width="100%">
            <SvgIcon>
              <CheckCircleIcon
                htmlColor={stepCompletion() ? "#7551D0" : "#C1C1C1"}
              />
            </SvgIcon>
            <Stack spacing={2} width="100%">
              <Stack spacing={0.5}>
                <Typography variant="h4">Post on</Typography>
                <Typography variant="body1">Where to post?</Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Stack spacing={2}>
                    <CustomAutoCompleteNew
                      options={socialApps}
                      renderOption={(props, option) => {
                        return <li {...props}>{option.label}</li>;
                      }}
                      id={"from-section-email-address"}
                      onChange={(
                        event: any,
                        newValue: { label: string; value: string }
                      ) => handlePlatFormChange(event, newValue)}
                      value={formFields.selectedPlatform.value}
                      label="Platforms"
                      placeHolder="Select the platform"
                      error={formFields.selectedPlatform.error}
                      clearIcon={false}
                    />
                    <CustomAutoCompleteNew
                      options={pages}
                      renderOption={(props, option) => {
                        return <li {...props}>{option.label}</li>;
                      }}
                      id={"from-section-email-address"}
                      onChange={(event: any, newValue: any) => {
                        setFormFields({
                          ...formFields,
                          selectedPage: {
                            label: {
                              error: "",
                              value: newValue?.label ? newValue?.label : "",
                            },
                            userName: {
                              error: "",
                              value: newValue?.userName
                                ? newValue?.userName
                                : "",
                            },
                            pageId: {
                              error: "",
                              value: newValue?.value ? newValue?.value : "",
                            },
                            profilePicture: {
                              error: "",
                              value: newValue?.profile_picture ?? "",
                            },
                          },
                        });
                      }}
                      value={formFields.selectedPage.label.value}
                      label="Post to"
                      placeHolder="Select the page"
                      error={formFields.selectedPage.label.error}
                    />
                  </Stack>
                </Grid>
              </Grid>
            </Stack>
          </Stack>
          <Stack>
            <IconButton onClick={() => setManagingStep("")}>
              <SvgIcon>
                <CloseIcon />
              </SvgIcon>
            </IconButton>
          </Stack>
        </Stack>
        <Stack
          alignItems="center"
          justifyContent="flex-end"
          direction="row"
          spacing={1}
        >
          <Button
            size="small"
            variant="text"
            onClick={() => setManagingStep("")}
          >
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={handleSave}>
            Save
          </Button>
        </Stack>
      </Stack>
    );
  };

  const getNonExpandedView = () => {
    const onAnotherStep =
      managingStep !== "" && managingStep !== "from-section";
    return (
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ opacity: onAnotherStep ? 0.5 : 1 }}
      >
        <Stack direction="row" spacing={4}>
          <SvgIcon>
            <CheckCircleIcon
              htmlColor={stepCompletion() ? "#7551D0" : "#C1C1C1"}
            />
          </SvgIcon>
          <Stack spacing={0.5}>
            <Typography variant="h4">Post on</Typography>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography variant="subtitle1">
                {submitCampaign.socialAppName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                &#x25CF;
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {submitCampaign.socialSource}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
        <Stack>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setManagingStep("from-section");
            }}
            disabled={onAnotherStep}
          >
            Edit
          </Button>
        </Stack>
      </Stack>
    );
  };

  const getPage = () => {
    if (managingStep === "from-section") {
      return getExpandedView();
    }
    return getNonExpandedView();
  };

  const getFooter = () => {
    return (
      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setOpenConfirmation(false)}
        >
          No
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={() =>
            selectedPlatform && applyPlatformChange(selectedPlatform)
          }
        >
          Yes discard
        </Button>
      </Stack>
    );
  };

  return (
    <Box px={3}>
      {getPage()}
      <CustomDialog
        isDialogOpen={openConfirmation}
        handleDialogClose={() => setOpenConfirmation(false)}
        title={
          <Typography variant="h4" color="#7551D0">
            Please confirm
          </Typography>
        }
        body={
          <Stack spacing={3}>
            <Typography>Your changes will be discarded</Typography>
          </Stack>
        }
        footer={getFooter()}
        closeButtonVisibility
        closable
      />
    </Box>
  );
};

export default PostSection;
